/**
 * Hooks fürs Settings-Modul: Vor-Ort-Frequenzen pro (Segment × Quadrant) und
 * die Agenda-Vorlagen der Customer View (D9).
 * BCG-Schwellwerte werden ausschließlich auf der BCG-Seite gepflegt
 * (useBcg.ts) — der frühere Settings-Pfad schrieb stur rolling_12m und
 * divergierte vom Perioden-Kontext der Matrix.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  agendaTemplatesResponseSchema,
  type AgendaKind,
  type AgendaTemplateUpdate,
  type Quadrant,
} from "@msc/shared";

import { api } from "../lib/api.js";

export const settingsKeys = {
  frequencies: (segmentId: string | null) =>
    ["settings", "frequencies", segmentId ?? "default"] as const,
  agendaTemplates: (segmentId: string | null) =>
    ["settings", "agenda-templates", segmentId ?? "default"] as const,
};

// ── Vor-Ort-Frequenzen ──────────────────────────────────────────────────────

export interface FrequencyEntry {
  segmentId: string | null;
  quadrant: Quadrant;
  months: number | null;
}

export function useFrequencies(segmentId: string | null) {
  // segmentId === null → globale Defaults; sonst Override-Liste für dieses Segment
  const path =
    segmentId !== null
      ? `/settings/onsite-frequencies?segmentId=${segmentId}`
      : "/settings/onsite-frequencies";
  return useQuery<FrequencyEntry[]>({
    queryKey: settingsKeys.frequencies(segmentId),
    queryFn: () => api.get<FrequencyEntry[]>(path),
  });
}

export interface SetFrequencyPayload {
  segmentId: string | null;
  quadrant: Quadrant;
  months: number | null;
}

export function useSetFrequency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SetFrequencyPayload) =>
      api.put("/settings/onsite-frequencies", payload),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: settingsKeys.frequencies(variables.segmentId),
      });
      // Reminder-Ampel neu rechnen lassen
      void qc.invalidateQueries({ queryKey: ["visits"] });
    },
  });
}

// ── Agenda-Vorlagen der Customer View (D9) ──────────────────────────────────

/**
 * Titel, Markdown-Text und Budgetanzeige je Agenda-Punkt — global als
 * Standard (`segmentId: null`) oder als Override eines Bereichs. Die Antwort
 * ist immer vollständig: Wo kein Override existiert, steht die Standardzeile
 * mit `isOverride: false`.
 *
 * Wie bei den Frequenzen entscheidet `segmentId` über den Pfad; gelesen
 * werden darf von jedem Angemeldeten (D9: alle Leads pflegen die Vorlagen).
 */
export function useAgendaTemplates(segmentId: string | null) {
  const path =
    segmentId !== null
      ? `/settings/customer-view-agenda-templates?segmentId=${encodeURIComponent(segmentId)}`
      : "/settings/customer-view-agenda-templates";
  return useQuery({
    queryKey: settingsKeys.agendaTemplates(segmentId),
    queryFn: async () => agendaTemplatesResponseSchema.parse(await api.get(path)),
  });
}

/**
 * Beide betroffenen Listen neu ziehen: die des Bereichs **und** die des
 * Standards. Ein Override ändert zwar nur die eine, aber wer im Panel
 * zwischen „Standard" und einem Bereich hin- und herschaltet, sieht sonst
 * einen Stand, den es nicht mehr gibt — die Standardliste ist die Quelle, aus
 * der die Bereichsliste ihre nicht überschriebenen Zeilen bekommt.
 */
function invalidateAgendaTemplates(
  qc: ReturnType<typeof useQueryClient>,
  segmentId: string | null,
): void {
  void qc.invalidateQueries({ queryKey: settingsKeys.agendaTemplates(segmentId) });
  if (segmentId !== null) {
    void qc.invalidateQueries({ queryKey: settingsKeys.agendaTemplates(null) });
  }
}

/** Standard anlegen/ändern oder einen Override des Bereichs setzen (PUT, 204). */
export function useSaveAgendaTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgendaTemplateUpdate) =>
      api.put("/settings/customer-view-agenda-templates", payload),
    onSuccess: (_data, variables) => invalidateAgendaTemplates(qc, variables.segmentId),
  });
}

/**
 * Den Override eines Bereichs entfernen — danach greift wieder der Standard.
 * `segmentId` ist Pflicht (einen Standard kann man ändern, aber nicht
 * löschen), beides reist als Abfrageparameter, weil DELETE keinen Body trägt.
 */
export function useDeleteAgendaOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ segmentId, kind }: { segmentId: string; kind: AgendaKind }) =>
      api.delete(
        `/settings/customer-view-agenda-templates?segmentId=${encodeURIComponent(segmentId)}&kind=${encodeURIComponent(kind)}`,
      ),
    onSuccess: (_data, variables) => invalidateAgendaTemplates(qc, variables.segmentId),
  });
}
